/**
 * Minister profile → "Eventos": every event the minister registered for, plus those where a
 * delegate attended on their behalf. "Ver" opens the filled form and receipt (with print / PDF).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { getMinisterEventRegistrations, type MinisterEventRegistration } from '@/Services/eventParticipants.service';
import { getPublicRegistration } from '../public/publicRegistrationRepository';
import { RegistrationDocumentViewer } from '../public/components/RegistrationDocumentViewer';
import type { PublicRegistration } from '../public/types';

const REGISTRATION_LABELS = {
  PRE_REGISTERED: { text: 'Pendiente', variant: 'secondary' },
  VERIFIED: { text: 'Verificada', variant: 'default' },
  REJECTED: { text: 'Observada', variant: 'destructive' },
} as const;

const formatDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('es-BO');

export function MinisterEventsTab({ ministerId }: { ministerId: string }) {
  const { toast } = useToast();
  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ['minister_events', ministerId],
    queryFn: () => getMinisterEventRegistrations(ministerId),
  });
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<{ registration: PublicRegistration; token: string } | null>(null);

  const open = async (row: MinisterEventRegistration) => {
    setOpeningId(row.participantId);
    try {
      setViewing({ registration: await getPublicRegistration(row.publicToken), token: row.publicToken });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'No se pudo abrir el formulario', variant: 'destructive' });
    } finally {
      setOpeningId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error) return <p className="text-sm text-destructive">No se pudo cargar el historial de eventos.</p>;
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">Este ministro no tiene eventos registrados.</p>;

  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-muted/60 text-left">
              <th className="px-3 py-2 font-semibold">Evento</th>
              <th className="px-3 py-2 font-semibold">Fecha</th>
              <th className="px-3 py-2 font-semibold">Participó como</th>
              <th className="px-3 py-2 text-center font-semibold">Inscripción</th>
              <th className="px-3 py-2 text-center font-semibold">Asistencia</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const reg = REGISTRATION_LABELS[r.registrationStatus];
              return (
                <tr key={r.participantId} className="border-t border-border">
                  <td className="px-3 py-2">
                    <p className="font-medium">{r.event.name}</p>
                    {r.event.location && <p className="text-xs text-muted-foreground">{r.event.location}</p>}
                  </td>
                  <td className="px-3 py-2">{formatDate(r.event.start_date)}</td>
                  <td className="px-3 py-2">
                    {r.role === 'MINISTER' ? 'Ministro' : `Representado por ${r.delegateName ?? 'un delegado'}`}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Badge variant={reg.variant}>{reg.text}</Badge>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {r.attendanceStatus === 'ATTENDED' ? <Badge>Asistió</Badge> : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" disabled={openingId === r.participantId} onClick={() => open(r)}>
                      {openingId === r.participantId ? (
                        <Loader2 className="mr-1.5 size-4 animate-spin" />
                      ) : (
                        <FileText className="mr-1.5 size-4" />
                      )}
                      Formulario / recibo
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {viewing && (
        <RegistrationDocumentViewer
          registration={viewing.registration}
          token={viewing.token}
          variant="portal"
          onClose={() => setViewing(null)}
        />
      )}
    </>
  );
}
